import {objectFromEntries, groupBy} from '@benchling/js-utils';

import {CodonTranslationTable, DEGENERATE_BASES, StandardDNABase} from 'biojs/BioConstants';
import geneticCodesDNA from 'data/geneticCodesDNA.json';

export enum AbbreviationFormat {
  SINGLE_LETTER = 'SLA',
  TRIPLE_LETTER = 'TLA',
}
export const AMINO_ACID_ABBREVIATION_PREF_KEY = 'amino_acid_select.abbreviation';

// https://www.chempep.com/ChemPep-Amino-Acid-Standard-List.htm
const AMINO_ACID_TLA = {
  '*': '*',
  A: 'Ala',
  B: 'Asx', // D or N
  C: 'Cys',
  D: 'Asp',
  E: 'Glu',
  F: 'Phe',
  G: 'Gly',
  H: 'His',
  I: 'Ile',
  J: 'Xle', // I or L
  K: 'Lys',
  L: 'Leu',
  M: 'Met',
  N: 'Asn',
  O: 'Pyl',
  P: 'Pro',
  Q: 'Gln',
  R: 'Arg',
  S: 'Ser',
  T: 'Thr',
  U: 'Sec',
  V: 'Val',
  W: 'Trp',
  X: 'Xaa',
  Y: 'Tyr',
  Z: 'Glx', // Q or E
};

// This should be kept in sync with SPECIAL_AMINO_ACIDS in molbio/proteins/alignments.py.
// Amino acid letters that are either degenerate or not part of the 20 normal amino acids found in humans.
export type NonStandardAminoAcids = 'B' | 'J' | 'O' | 'U' | 'X' | 'Z';
export const NON_STANDARD_AMINO_ACIDS = ['B', 'J', 'O', 'U', 'X', 'Z'];

export type StandardAminoAcidSLA = Exclude<keyof typeof AMINO_ACID_TLA, NonStandardAminoAcids>;

export type StandardDNACodon = `${StandardDNABase}${StandardDNABase}${StandardDNABase}`;

export interface GeneticCodeData {
  number: number;
  name: string;
  code: {[codon: string]: StandardAminoAcidSLA};
  taxonomy: Array<string>;
}

// TODO: Create degenerate codon type
const DEGENERATE_DNA_CODE: {[codon: string]: StandardAminoAcidSLA} =
  geneticCodesDNA[CodonTranslationTable.STANDARD].code;

const STANDARD_DNA_CODE: {[codon in StandardDNACodon]: StandardAminoAcidSLA} = objectFromEntries(
  Object.entries(DEGENERATE_DNA_CODE).filter(
    ([codon, sla]) => !Array.from(codon).some((base) => DEGENERATE_BASES.includes(base))
  )
) as {[codon in StandardDNACodon]: StandardAminoAcidSLA};

const AMINO_ACID_TO_STANDARD_DNA_CODONS: Record<StandardAminoAcidSLA, Array<StandardDNACodon>> = groupBy(
  Object.keys(STANDARD_DNA_CODE),
  (key: StandardDNACodon) => STANDARD_DNA_CODE[key]
) as Record<StandardAminoAcidSLA, Array<StandardDNACodon>>;

const AMINO_ACID_TO_DEGENERATE_DNA_CODONS: Record<StandardAminoAcidSLA, Array<string>> = groupBy(
  Object.keys(DEGENERATE_DNA_CODE),
  (key: string) => DEGENERATE_DNA_CODE[key]
) as Record<StandardAminoAcidSLA, Array<string>>;

export type DNAStartCodon = 'ATG' | 'TTG' | 'CTG' | 'GTG';

const DEFAULT_DNA_START_CODON: DNAStartCodon = 'ATG';
// TODO: these were introduced before we had a concept of Alternate Codon Tables. Should find a way to
// reconcile them for a better user + developer experience.
export const DNA_START_CODONS: Array<DNAStartCodon> = ['ATG', 'TTG', 'CTG', 'GTG'];
export const ALL_CODONS: Array<StandardDNACodon> = [
  'AAA',
  'AAC',
  'AAG',
  'AAT',
  'ACA',
  'ACC',
  'ACG',
  'ACT',
  'AGA',
  'AGC',
  'AGG',
  'AGT',
  'ATA',
  'ATC',
  'ATG',
  'ATT',
  'CAA',
  'CAC',
  'CAG',
  'CAT',
  'CCA',
  'CCC',
  'CCG',
  'CCT',
  'CGA',
  'CGC',
  'CGG',
  'CGT',
  'CTA',
  'CTC',
  'CTG',
  'CTT',
  'GAA',
  'GAC',
  'GAG',
  'GAT',
  'GCA',
  'GCC',
  'GCG',
  'GCT',
  'GGA',
  'GGC',
  'GGG',
  'GGT',
  'GTA',
  'GTC',
  'GTG',
  'GTT',
  'TAA',
  'TAC',
  'TAG',
  'TAT',
  'TCA',
  'TCC',
  'TCG',
  'TCT',
  'TGA',
  'TGC',
  'TGG',
  'TGT',
  'TTA',
  'TTC',
  'TTG',
  'TTT',
];
const DNA_STOP_CODONS: Array<string> = ['TAG', 'TAA', 'TGA'];

export {
  AMINO_ACID_TLA,
  DEFAULT_DNA_START_CODON,
  DNA_STOP_CODONS,
  STANDARD_DNA_CODE,
  DEGENERATE_DNA_CODE,
  AMINO_ACID_TO_STANDARD_DNA_CODONS,
  AMINO_ACID_TO_DEGENERATE_DNA_CODONS,
};
